import { useRef, useState } from 'react';
import type { Demande } from '../../../types';
import { MessagesModal } from '../../messages/MessagesModal';
import { formatDateCourt } from '../format';
import { StatutBadge } from '../StatutBadge';
import { useFenetreModale } from '../useFenetreModale';
import { pluriel, type Voix } from './textesDemande';
import type { ResultatEnvoi } from './useNouvelleDemande';

const STATUTS_RECHERCHE = ['recherche_interne_1', 'recherche_interne_2'];

interface Props {
  resultat: ResultatEnvoi;
  voix: Voix;
  libelleStatut: (s: Demande['statut']) => string | undefined;
  /** Un aperçu ou une fenêtre est ouvert PAR-DESSUS : Échap le ferme LUI, pas cette confirmation. */
  parDessus: boolean;
  onApercu: (d: Demande) => void;
  onNouvelle: () => void;
  onTableauDeBord: () => void;
}

/**
 * Fin de la demande : dit ce qui a VRAIMENT été créé et ce qui part vraiment. Jamais « vos collègues sont prévenu·e·s » tant que le message n'est pas
 * publié (DECISIONS §24) ; les messages personnels disent à qui ils sont partis, et à qui ils ne peuvent pas partir (compte absent, message coupé, panne).
 * Un brouillon repéré dans Tipee n'est pas « rien à refaire » : il attend l'envoi de la personne.
 * « Tableau de bord » est le choix par défaut (Échap, ✕, clic hors de la fenêtre).
 */
export function ConfirmationEnvoi({ resultat, voix, libelleStatut, parDessus, onApercu, onNouvelle, onTableauDeBord }: Props) {
  const { creees, doublons, dejaDemandees, prevenus = [], sans_email: sansEmail = [], slack } = resultat;
  // Déjà demandées : celles qu'on n'a pas envoyées (le serveur ne les connaît pas) + celles qu'il a trouvées en doublon (créées entre-temps ailleurs).
  // Un brouillon Tipee à envoyer est dit à part : il n'est pas pris en charge tant que la personne ne l'a pas envoyé.
  const aEnvoyer = dejaDemandees.filter((s) => s.aEnvoyer);
  const dejaFaites = dejaDemandees.filter((s) => !s.aEnvoyer);
  const [messages, setMessages] = useState<Demande | null>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const { ref } = useFenetreModale(onTableauDeBord, { focusInitial: () => bouton.current, auDessus: () => parDessus || messages != null });
  const nbDeja = doublons.length + dejaFaites.length;
  const total = creees.length + nbDeja;
  const aPublier = creees.filter((d) => STATUTS_RECHERCHE.includes(d.statut));

  // Messages personnels : un par personne, quel que soit le nombre de séances.
  const envoyes = Array.from(new Set(prevenus.filter((p) => p.etat === 'envoye').map((p) => p.nom)));
  const sansCompte = Array.from(new Set(prevenus.filter((p) => p.etat === 'sans_compte').map((p) => p.nom)));
  const nonPrevenus = Array.from(new Set(prevenus.filter((p) => p.etat === 'non_prevenu').map((p) => p.nom)));
  // « en attente » après une création : le message n'est pas parti (panne) et rien ne le renverra tout seul.
  const pasParti = Array.from(new Set(prevenus.filter((p) => p.etat === 'en_attente').map((p) => p.nom)));

  return (
    <>
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onTableauDeBord(); }}>
      <div className="modal modal-wide" role="dialog" aria-modal="true" aria-labelledby="nd-conf-t" ref={ref} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title" id="nd-conf-t">
            {creees.length === 1 && doublons.length === 0 ? 'Demande enregistrée' : `${pluriel(creees.length, 'demande')} enregistrée${creees.length > 1 ? 's' : ''}`}
          </h3>
          <button type="button" className="close-btn" onClick={onTableauDeBord} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body">
          <ul className="nd-recap">
            {creees.map((d) => (
              <li key={d.id}>
                <span className="nd-r-nom">
                  {d.atelier_nom}
                  <small>{formatDateCourt(d.date_atelier)}{d.atelier_lieu ? ` · ${d.atelier_lieu}` : ''}{d.remplacant_nom ? ` · ${d.remplacant_nom}` : ''}</small>
                </span>
                <StatutBadge statut={d.statut} libelle={libelleStatut(d.statut)} compact sansInfobulle />
                <span className="nd-r-actions">
                  <button type="button" className="btn btn-sm btn-ghost" aria-label={`Voir la demande ${d.atelier_nom}, ${formatDateCourt(d.date_atelier)}`} onClick={() => onApercu(d)}>Voir</button>
                  {slack === 'a_envoyer' && STATUTS_RECHERCHE.includes(d.statut) && (
                    <button type="button" className="btn btn-sm btn-ghost" aria-label={`Message Slack : voir le message à publier, ${d.atelier_nom}, ${formatDateCourt(d.date_atelier)}`} onClick={() => setMessages(d)}>Message Slack</button>
                  )}
                </span>
              </li>
            ))}
          </ul>

          {aEnvoyer.length > 0 && (
            <div className="nd-recap-deja">
              <p className="nd-aide">
                <strong>{aEnvoyer.length > 1 ? `${aEnvoyer.length} séances repérées dans Tipee attendent votre envoi` : 'Une séance repérée dans Tipee attend votre envoi'}</strong>
                {' '}: ouvrez-{aEnvoyer.length > 1 ? 'les' : 'la'} (bouton « Voir »), puis « Envoyer ma demande de remplacement ».
              </p>
              <ul className="nd-recap">
                {aEnvoyer.map((s) => (
                  <li key={s.cle}>
                    <span className="nd-r-nom">{s.atelierNom}<small>{formatDateCourt(s.date)}{s.lieu ? ` · ${s.lieu}` : ''}</small></span>
                    <span className="badge badge-warn">À envoyer</span>
                    {s.existante && <button type="button" className="btn btn-sm btn-ghost" aria-label={`Voir la demande ${s.atelierNom}, ${formatDateCourt(s.date)}`} onClick={() => onApercu(s.existante!)}>Voir</button>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {nbDeja > 0 && (
            <div className="nd-recap-deja">
              <p className="nd-aide">{nbDeja > 1 ? `${nbDeja} séances étaient déjà demandées : rien à refaire.` : 'Cette séance était déjà demandée : rien à refaire.'}</p>
              <ul className="nd-recap">
                {dejaFaites.map((s) => (
                  <li key={s.cle}>
                    <span className="nd-r-nom">{s.atelierNom}<small>{formatDateCourt(s.date)}{s.lieu ? ` · ${s.lieu}` : ''}</small></span>
                    {s.existante ? <StatutBadge statut={s.existante.statut} libelle={libelleStatut(s.existante.statut)} compact sansInfobulle /> : <span className="badge badge-warn">Déjà demandé</span>}
                    {s.existante && <button type="button" className="btn btn-sm btn-ghost" aria-label={`Voir la demande ${s.atelierNom}, ${formatDateCourt(s.date)}`} onClick={() => onApercu(s.existante!)}>Voir</button>}
                  </li>
                ))}
                {doublons.map((d) => d.acces === 'prise_en_charge' ? (
                  <li key={`${d.atelier_id}_${d.date_atelier}`}>
                    <span className="nd-r-nom">{d.atelier_nom}<small>{formatDateCourt(d.date_atelier)}</small></span>
                    <span className="badge badge-warn">Déjà demandé</span>
                  </li>
                ) : (
                  <li key={d.id}>
                    <span className="nd-r-nom">{d.atelier_nom}<small>{formatDateCourt(d.date_atelier)}</small></span>
                    <StatutBadge statut={d.statut} libelle={libelleStatut(d.statut)} compact sansInfobulle />
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => onApercu(d)}>Voir</button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Ce qui part vraiment */}
          {aPublier.length > 0 && slack === 'envoi_en_cours' && (
            <p className="nd-aide">{aPublier.length > 1 ? `Les ${aPublier.length} messages pour ${voix.pourMoi ? 'vos' : 'les'} collègues partent sur Slack.` : `Le message pour ${voix.pourMoi ? 'vos' : 'les'} collègues part sur Slack.`}</p>
          )}
          {aPublier.length > 0 && slack !== 'envoi_en_cours' && (
            <p className="nd-aide">
              {aPublier.length > 1
                ? `${aPublier.length} messages pour ${voix.pourMoi ? 'vos' : 'les'} collègues sont prêts : ${voix.pourMoi ? 'publiez-les' : 'à vous de les publier'} sur Slack, un par demande (bouton « Message »).`
                : `Un message pour ${voix.pourMoi ? 'vos' : 'les'} collègues est prêt : ${voix.pourMoi ? 'publiez-le' : 'à vous de le publier'} sur Slack (bouton « Message »).`}
            </p>
          )}
          {envoyes.length > 0 && <p className="nd-aide">Message personnel envoyé à {envoyes.join(', ')}.</p>}
          {sansCompte.length > 0 && <p className="nd-aide">{sansCompte.join(', ')} {sansCompte.length > 1 ? "n'ont pas de compte" : "n'a pas de compte"} : pas de message personnel. Pensez à prévenir {sansCompte.length > 1 ? 'ces personnes' : sansCompte[0]} directement.</p>}
          {nonPrevenus.length > 0 && <p className="nd-aide">{nonPrevenus.join(', ')} {nonPrevenus.length > 1 ? "n'ont pas pu être prévenu·e·s" : "n'a pas pu être prévenu·e"} personnellement.</p>}
          {pasParti.length > 0 && <p className="nd-aide">Le message personnel n'est pas parti pour {pasParti.join(', ')} : pensez à {pasParti.length > 1 ? 'les' : 'le/la'} prévenir directement.</p>}
          {sansEmail.length > 0 && creees.filter((d) => sansEmail.includes(d.id)).map((d) => (
            <p className="nd-aide" key={d.id}>{d.remplacant_nom ?? 'La personne désignée'} n'a pas d'adresse e-mail : aucune notification ne lui est envoyée pour le {formatDateCourt(d.date_atelier)}. Pensez à la ou le prévenir directement.</p>
          ))}

          {total > 1 && <p className="nd-aide">{pluriel(total, 'séance')} au total prise{total > 1 ? 's' : ''} en charge.</p>}
          <p className="nd-aide">{total > 1 ? 'Retrouvez-les' : 'Retrouvez-la'} à tout moment dans le Tableau de bord.</p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-sm" onClick={onNouvelle}>Nouvelle demande</button>
          <button type="button" className="btn btn-sm btn-accent" ref={bouton} onClick={onTableauDeBord}>Tableau de bord</button>
        </div>
      </div>
    </div>
    {messages && <MessagesModal demandeId={messages.id} titreSeance={`${messages.atelier_nom}, ${formatDateCourt(messages.date_atelier)}`} onClose={() => setMessages(null)} onChange={() => {}} />}
    </>
  );
}
